import React from 'react';
import cn from 'classnames';
import { MONTH_YEAR_FORMAT } from '@dealroadshow/uikit-date-time/dateFormats';
import { format, getQuarter, getYear } from 'date-fns';
import { Periods } from '@/creditflowresearch/ui/common/constants';
import getUTCDate from '@framework/date-helpers/getUTCDate';
import { roundUpValueToFraction } from '@framework/data-helpers/number/roundUpValueToFraction';
import styles from './chartTooltip.module.scss';

const getDateForHeader = (period, points) => {
  let date = new Date(points[0].x);

  if (period === Periods.YEAR) {
    return points[0].x;
  }
  if (period === Periods.QUARTER) {
    let quarter = getQuarter(getUTCDate(new Date(date)));
    let year = getYear(getUTCDate(new Date(date)));

    return `${ quarter }Q ${ year }`;
  }
  if (period === Periods.MONTH) {
    return format(getUTCDate(new Date(date)), MONTH_YEAR_FORMAT);
  }

  return undefined;
};

const localeParseFloat = (str) => {
  let out = [];
  str.split('.').forEach((x) => {
    x = x.replaceAll(',', '');
    out.push(x);
  });
  return roundUpValueToFraction(parseFloat(out.join(',')) / 1000, 1);
};

interface IPoint {
  color: string,
  colorIndex: number | undefined,
  key: number,
  percentage: number | undefined,
  point: {
    x: number,
    y: number,
    dealCount: number,
    dealSize: number,
    percentage: number,
  },
  series: { name: string },
  total: number | undefined,
  x: number,
  y: number,
}

interface IProps {
  showStackedData: boolean,
  period: string,
  showVolume: boolean,
  points: IPoint[],
}

const ChartTooltip = ({ showStackedData, period, showVolume, points }: IProps) => {
  const layout = [];
  let sortedData = points.sort((a, b) => a.y - b.y);

  const itemClassName = cn(
    styles.item,
    {
      [styles.withoutDealSize]: !showVolume,
      [styles.itemStacked]: showStackedData,
      [styles.itemGrouped]: !showStackedData,
    },
  );

  const itemDealCountClassName = cn(
    styles.itemDealCount,
    { [styles.itemDealCountIncreased]: showVolume },
  );

  /**
   * Item row
   */
  sortedData.forEach((item) => {
    if (item.point.y > 0) {
      layout.unshift(
        <div className={ itemClassName }>
          <div className={ styles.itemName }>
            <span
              className={ styles.itemSquare }
              style={ { backgroundColor: item.color } }
            />
            { item.series.name }
          </div>
          <div className={ itemDealCountClassName }>
            { item.point.dealCount }
          </div>
          { showVolume && (
            <div className={ styles.itemDealSize }>
              ${ localeParseFloat(item.point.dealSize.toLocaleString()) }b
            </div>
          ) }
          { showStackedData && (
            <div className={ styles.itemPercent }>
              { item.point.percentage.toFixed(1) }%
            </div>
          ) }
        </div>,
      );
    } else {
      layout.push(
        <div className={ itemClassName }>
          <div className={ styles.itemName }>
            <span
              className={ styles.itemSquare }
              style={ { backgroundColor: item.color } }
            />
            { item.series.name }
          </div>
          <div className={ itemDealCountClassName }>-</div>
          { showVolume && (
            <div className={ styles.itemDealSize }>-</div>
          ) }
          { showStackedData && (
            <div className={ styles.itemPercent }>-</div>
          ) }
        </div>,
      );
    }
  });

  const getCalculatedDealCount = points.reduce((sum, current) => sum + current.point.dealCount, 0);
  const getCalculatedDealSize = points.reduce((sum, current) => sum + current.point.dealSize, 0);

  const headerClassName = cn(
    styles.item,
    styles.header,
    {
      [styles.withoutDealSize]: !showVolume,
      [styles.itemStacked]: showStackedData,
      [styles.itemGrouped]: !showStackedData,
    },
  );

  /**
   * Header (1st row)
   */
  layout.unshift(
    <div className={ headerClassName }>
      <span className={ cn(styles.itemName, styles.headerDate) }>
        { getDateForHeader(period, points) }
      </span>
      <span className={ itemDealCountClassName }>
        { getCalculatedDealCount }
      </span>
      { showVolume && (
        <span className={ styles.itemDealSize }>
            { localeParseFloat(getCalculatedDealSize.toLocaleString()) }b
        </span>
      ) }
      { showStackedData && (
        <span className={ styles.itemPercent } />
      ) }
    </div>,
  );

  return layout;
};

export default ChartTooltip;
